/**
 * Search form for 統計一覧表 — /rezepts/statistical_init.
 *
 * Fields: 提出年月 (month picker, required, default current month) + 施術師 dropdown.
 * A2/R2 label swap: はり・きゅう師 ↔ マッサージ師.
 *
 * Slice 1 scope: skeleton + validation alerts.
 * Massager dropdown is rendered with caller-supplied options; data fetching
 * is wired in Slice 2.
 */

import React, { useCallback, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { AppButton, AppForm, AppSelect } from '@/components/atoms';
import { Search } from '@/components/atoms/AppIcon';
import CollapsibleCard from '@/components/molecules/CollapsibleCard';
import { RA2NDatePicker } from '@/components/organisms';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import {
  statisticalSearchSchema,
  type StatisticalSearchSchemaValues,
} from '@/schemas/statisticalReportSchema';
import { convGtJMonth, warekiMonthToYearmonth } from '@/utils/dateConverter';
import type { StatisticalMassagerOption, StatisticalSearchFormValues } from '../types';

interface Props {
  onSearch: (values: StatisticalSearchFormValues) => void;
  onYearmonthChange?: (yearmonth: string) => void;
  massagers: StatisticalMassagerOption[];
  serviceType: string;
  isLoading?: boolean;
  initialValues?: StatisticalSearchFormValues | null;
}

const StatisticalReportSearchForm: React.FC<Props> = ({
  onSearch,
  onYearmonthChange,
  massagers,
  serviceType,
  isLoading = false,
  initialValues,
}) => {
  const isA2 = serviceType === SERVICE_TYPE_A2;
  const massagerLabel = isA2 ? 'はり・きゅう師' : 'マッサージ師';

  const defaultYearmonthWareki = useMemo(() => convGtJMonth(dayjs()), []);

  const defaultValues = useMemo<StatisticalSearchSchemaValues>(
    () => ({
      searchYearmonthWareki:
        initialValues?.searchYearmonth && initialValues.searchYearmonth.length === 6
          ? convGtJMonth(
              dayjs(
                `${initialValues.searchYearmonth.slice(0, 4)}-${initialValues.searchYearmonth.slice(4)}-01`
              )
            )
          : defaultYearmonthWareki,
      massagerId: initialValues?.massagerId ?? null,
    }),
    [defaultYearmonthWareki, initialValues]
  );

  const { control, handleSubmit } = useTrimmedForm<StatisticalSearchSchemaValues>({
    resolver: zodResolver(statisticalSearchSchema),
    defaultValues,
    mode: 'onSubmit',
  });

  const massagerOptions = useMemo(
    () => [
      { value: '', label: 'すべて' },
      ...massagers.map(m => ({
        value: String(m.id),
        label: `${m.serial_number ?? ''}:${m.name}`,
      })),
    ],
    [massagers]
  );

  const handleValidatedSearch = useCallback(
    (values: StatisticalSearchSchemaValues) => {
      const yyyymm = warekiMonthToYearmonth(values.searchYearmonthWareki);
      onSearch({
        searchYearmonth: yyyymm === undefined ? values.searchYearmonthWareki : String(yyyymm),
        massagerId: values.massagerId,
      });
    },
    [onSearch]
  );

  const submit = handleSubmit(handleValidatedSearch);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        void submit();
      }
    },
    [submit]
  );

  return (
    <CollapsibleCard title="統計一覧表">
      <div onKeyDown={handleKeyDown}>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-[minmax(0,260px)_minmax(0,320px)_auto] xl:items-start">
          <label className="flex flex-col min-w-0 gap-1 text-sm sm:flex-row sm:items-start sm:gap-2">
            <span className="shrink-0 pt-1.5 sm:w-20">提出年月</span>
            <div className="w-full min-w-0 sm:flex-1">
              <Controller
                name="searchYearmonthWareki"
                control={control}
                render={({ field, fieldState }) => (
                  <AppForm.Item
                    className="mb-0"
                    validateStatus={fieldState.invalid ? 'error' : undefined}
                    help={fieldState.error?.message}
                  >
                    <RA2NDatePicker
                      picker="month"
                      value={field.value}
                      onChange={val => {
                        field.onChange(val);
                        const yyyymm = warekiMonthToYearmonth(val);
                        if (yyyymm !== undefined) {
                          onYearmonthChange?.(String(yyyymm));
                        }
                      }}
                      error={fieldState.invalid}
                      className="w-full"
                    />
                  </AppForm.Item>
                )}
              />
            </div>
          </label>

          <label className="flex flex-col min-w-0 gap-1 text-sm sm:flex-row sm:items-start sm:gap-2">
            <span className="shrink-0 pt-1.5 sm:w-28">{massagerLabel}</span>
            <div className="w-full min-w-0 sm:flex-1">
              <Controller
                name="massagerId"
                control={control}
                render={({ field }) => (
                  <AppSelect
                    className="w-full"
                    value={field.value === null ? '' : String(field.value)}
                    onChange={val => {
                      const next = val === '' || val == null ? null : Number(val);
                      field.onChange(next);
                    }}
                    options={massagerOptions}
                  />
                )}
              />
            </div>
          </label>
          <AppButton
            className="w-fit xl:mt-0"
            type="primary"
            onClick={() => void submit()}
            loading={isLoading}
          >
            <Search /> 検索
          </AppButton>
        </div>
      </div>
    </CollapsibleCard>
  );
};

export default StatisticalReportSearchForm;
